import * as z from 'zod';
import { HordePromptAnswerSchema, IdSchema, TimestampSchema } from './horde-schema-primitives';

export const CorePhaseSchema = z.enum(['command', 'movement', 'shooting', 'charge', 'fight']);
export type CorePhase = z.infer<typeof CorePhaseSchema>;

export const ResolutionModeSchema = z.enum(['automatic', 'random', 'tableInput', 'delegated']);
export type ResolutionMode = z.infer<typeof ResolutionModeSchema>;

export const GameLifecycleSchema = z.enum(['setup', 'active', 'resolvingEndGame', 'completed', 'abandoned']);
export type GameLifecycle = z.infer<typeof GameLifecycleSchema>;

export const RulesetReferenceSchema = z.object({
    contentManifestHash: z.string().min(1),
    id: z.literal('horde-11e-v0.9'),
    sourceSha256: z.string().min(1),
});
export type RulesetReference = z.infer<typeof RulesetReferenceSchema>;

export const RulesInterpretationConfigSchema = z.object({
    deepStrikeRoundPolicy: z.literal('rounds2To4Only'),
    infiniteEndPolicy: z.literal('manualOnly'),
    ingressOrderPolicy: z.literal('beforeNormalMovement'),
    movementChargeTiePolicy: z.enum(['tableChoice', 'random']),
    multiFactionPrimaryRulePolicy: z.literal('matchingFactionKeywords'),
    multipleClearedHordeUnitsPolicy: z.literal('sumBracketMinimums'),
    multipleShootingExceptionsPolicy: z.literal('perWeaponThenTableChoice'),
    recordHordeBattleShock: z.literal(true),
    recoverLostRelicTimingPolicy: z.literal('nextCommandPhaseEnd'),
});
export type RulesInterpretationConfig = z.infer<typeof RulesInterpretationConfigSchema>;

export const DEFAULT_HORDE_RULES_INTERPRETATIONS: RulesInterpretationConfig = {
    deepStrikeRoundPolicy: 'rounds2To4Only',
    infiniteEndPolicy: 'manualOnly',
    ingressOrderPolicy: 'beforeNormalMovement',
    movementChargeTiePolicy: 'tableChoice',
    multiFactionPrimaryRulePolicy: 'matchingFactionKeywords',
    multipleClearedHordeUnitsPolicy: 'sumBracketMinimums',
    multipleShootingExceptionsPolicy: 'perWeaponThenTableChoice',
    recordHordeBattleShock: true,
    recoverLostRelicTimingPolicy: 'nextCommandPhaseEnd',
};

export const EventMetadataSchema = z.object({
    actorDeviceId: IdSchema,
    actorUserId: IdSchema,
    causationId: IdSchema.optional(),
    committedByHostDeviceId: IdSchema,
    correlationId: IdSchema.optional(),
    createdAt: TimestampSchema,
    eventId: IdSchema,
    expectedRevision: z.number().int().nonnegative(),
    gameId: IdSchema,
    visibility: z.union([
        z.literal('public'),
        z.object({
            privateToPlayerId: IdSchema,
        }),
    ]),
});
export type EventMetadata = z.infer<typeof EventMetadataSchema>;

export const AppliedModifierSchema = z.object({
    id: IdSchema,
    label: z.string().min(1),
    sourceId: IdSchema,
    value: z.number(),
});
export type AppliedModifier = z.infer<typeof AppliedModifierSchema>;

export interface SpawnCompositionNode {
    bracketId: string;
    bracketMinimum: number;
    children: SpawnCompositionNode[];
    id: string;
    selectedUnitId: string | null;
}

export const SpawnCompositionNodeSchema: z.ZodType<SpawnCompositionNode> = z.lazy(() =>
    z.object({
        bracketId: IdSchema,
        bracketMinimum: z.number().int(),
        children: z.array(SpawnCompositionNodeSchema),
        id: IdSchema,
        selectedUnitId: IdSchema.nullable(),
    })
);

export const ClearedUnitRecordSchema = z.object({
    bracketMinimum: z.number().int(),
    factionId: IdSchema,
    hordeUnitId: IdSchema.optional(),
    playerUnitRef: IdSchema.optional(),
});
export type ClearedUnitRecord = z.infer<typeof ClearedUnitRecordSchema>;

export const SpawnSourceSchema = z.enum([
    'setup',
    'roundStart',
    'pincerManeuver',
    'reinforcementsArrive',
    'fieldPromotion',
    'factionRespawn',
    'deployFortification',
]);
export type SpawnSource = z.infer<typeof SpawnSourceSchema>;

export const SpawnWorkflowSchema = z.object({
    bracketId: IdSchema.nullable(),
    clearedUnits: z.array(ClearedUnitRecordSchema),
    completedZoneIds: z.array(IdSchema),
    composition: z.array(SpawnCompositionNodeSchema),
    currentZoneId: IdSchema.nullable(),
    dice: z.array(z.number().int().positive()).nullable(),
    modifiedTotal: z.number().int().nullable(),
    modifiers: z.array(AppliedModifierSchema),
    placementCursor: z.number().int().nonnegative(),
    requiredZoneIds: z.array(IdSchema),
    round: z.number().int().positive(),
    source: SpawnSourceSchema,
    status: z.enum(['selectingZone', 'clearing', 'rolling', 'selecting', 'placing', 'complete']),
    unmodifiedTotal: z.number().int().nullable(),
});
export type SpawnWorkflow = z.infer<typeof SpawnWorkflowSchema>;

export const HordeInstructionSchema = z.object({
    action: z.enum([
        'remain',
        'normalMove',
        'advance',
        'fallBack',
        'disembark',
        'shoot',
        'charge',
        'pileIn',
        'fight',
        'consolidate',
        'useDatasheetAbility',
        'noEligibleAction',
    ]),
    delegatedResolutionText: z.string(),
    phase: CorePhaseSchema,
    reasonCodes: z.array(z.string()),
    targetRefs: z.array(IdSchema),
    unitId: IdSchema,
});
export type HordeInstruction = z.infer<typeof HordeInstructionSchema>;

export const AiTargetCandidateSchema = z.object({
    distanceRank: z.number().int().positive().nullable(),
    eligible: z.boolean(),
    id: IdSchema,
    kind: z.enum(['enemyUnit', 'objective', 'defenderEdge', 'weaponTarget']),
    reasonCodes: z.array(z.string()),
    visible: z.boolean().nullable(),
});
export type AiTargetCandidate = z.infer<typeof AiTargetCandidateSchema>;

export const HordeAiWorkflowSchema = z
    .object({
        candidates: z.array(AiTargetCandidateSchema),
        currentInstruction: HordeInstructionSchema.nullable(),
        facts: z.record(z.string().max(128), HordePromptAnswerSchema),
        modelGroupCursor: IdSchema.nullable(),
        phase: CorePhaseSchema,
        reasonTrace: z.array(z.string().min(1).max(128)),
        status: z.enum(['collectingFacts', 'instructing', 'awaitingResolution', 'complete']),
        unitId: IdSchema,
    })
    .strict();
export type HordeAiWorkflow = z.infer<typeof HordeAiWorkflowSchema>;

const SetupCursorSchema = z.object({
    kind: z.literal('setup'),
    spawn: SpawnWorkflowSchema.optional(),
    step: z.enum([
        'armyConstruction',
        'chooseDeployment',
        'assignSides',
        'createSpawnZones',
        'shuffleDecks',
        'chooseRoundLength',
        'dealSecretObjectives',
        'choosePrimaryFactionRule',
        'deployPlayers',
        'initialHordeSpawn',
        'startOfGameAbilities',
    ]),
});

const RoundStartCursorSchema = z.object({
    kind: z.literal('roundStart'),
    spawn: SpawnWorkflowSchema.optional(),
    step: z.enum([
        'discardActiveMisery',
        'activateQueuedMisery',
        'revealMisery',
        'revealSecondary',
        'resolveStartMissionEffects',
        'spawnHorde',
        'respawnSetAsideUnits',
        'resolveOtherStartEffects',
    ]),
});

const HordeTurnCursorSchema = z.object({
    ai: HordeAiWorkflowSchema.optional(),
    kind: z.literal('hordeTurn'),
    modelCursor: IdSchema.optional(),
    phase: CorePhaseSchema,
    step: z.string().min(1),
    unitCursor: IdSchema.optional(),
});

const PlayerTurnCursorSchema = z.object({
    kind: z.literal('playerTurn'),
    phase: z.enum(['command', 'movement', 'shooting', 'charge', 'fight', 'turnEnd']),
    step: z.enum(['corePlay', 'battleShock', 'awardObjectiveSp', 'resupply', 'resolveTimedActions']),
});

const RoundEndCursorSchema = z.object({
    kind: z.literal('roundEnd'),
    step: z.enum([
        'resolveSecondaryMissions',
        'applyRewardsAndPunishments',
        'resolveRoundEndEffects',
        'expireRoundEffects',
        'checkGameEnd',
        'prepareNextRound',
    ]),
});

const EndGameCursorSchema = z.object({
    kind: z.literal('endGame'),
    step: z.enum([
        'recordSurvival',
        'revealEndGameObjectives',
        'resolveEarlyRevealFailures',
        'resolveActionFailures',
        'resolveSurvivalRequirements',
        'resolveSecretObjectives',
        'publishOutcomes',
    ]),
});

const TerminalCursorSchema = z.object({
    kind: z.literal('terminal'),
    result: z.enum(['completed', 'abandoned']),
});

export const WorkflowCursorSchema = z.discriminatedUnion('kind', [
    SetupCursorSchema,
    RoundStartCursorSchema,
    HordeTurnCursorSchema,
    PlayerTurnCursorSchema,
    RoundEndCursorSchema,
    EndGameCursorSchema,
    TerminalCursorSchema,
]);
export type WorkflowCursor = z.infer<typeof WorkflowCursorSchema>;
